import { useEffect, useState, type MouseEvent } from "react";
import { ArrowRight, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import Logo from "./Logo";
import { CTA_LABEL, NAV_ITEMS } from "@/content/site";
import { createThrottledScrollHandler } from "@/utils/smoothScroll";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pendingHash, setPendingHash] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = createThrottledScrollHandler(() => setIsScrolled(window.scrollY > 8));
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fecha o menu primeiro; a rolagem acontece em onCloseAutoFocus
  const navigateFromMenu = (hash: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setPendingHash(hash);
    setIsMenuOpen(false);
  };

  const scrollToPendingHash = (event: Event) => {
    if (!pendingHash) return;
    event.preventDefault();
    document.querySelector(pendingHash)?.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", pendingHash);
    setPendingHash(null);
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300",
        isScrolled ? "border-ink-900/[0.06] bg-white/95 backdrop-blur-xl" : "border-transparent",
      )}
    >
      <div className="mx-auto flex h-[72px] max-w-page items-center justify-between px-5 sm:px-8">
        <a href="#" aria-label="Vexly AI, voltar ao início">
          <Logo />
        </a>

        <nav aria-label="Principal" className="hidden items-center gap-9 text-[15px] text-ink-600 lg:flex">
          {NAV_ITEMS.map((item) => (
            <a key={item.href} href={item.href} className="transition-colors hover:text-ink-900">
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <a
            href="#contact"
            className="btn-primary hidden px-5 py-2.5 text-[15px] shadow-[0_8px_20px_-8px_rgb(88_50_240/.7)] sm:inline-flex"
          >
            {CTA_LABEL}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>

          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Abrir menu"
                className="grid h-11 w-11 place-items-center rounded-full text-ink-900 lg:hidden"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="top"
              aria-describedby={undefined}
              onCloseAutoFocus={scrollToPendingHash}
              className="px-5 pb-8 pt-5"
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Logo />
              <nav aria-label="Principal" className="mt-8 flex flex-col divide-y divide-ink-900/[0.06]">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={navigateFromMenu(item.href)}
                    className="py-4 text-lg font-medium text-ink-900"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              <a href="#contact" onClick={navigateFromMenu("#contact")} className="btn-primary mt-6 w-full">
                {CTA_LABEL}
                <ArrowRight className="h-[18px] w-[18px]" aria-hidden="true" />
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
